import { enableProdMode, importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from '@app/app.component';
import { datadogRum } from '@datadog/browser-rum';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { Angulartics2Module } from 'angulartics2';
import { ServiceWorkerModule } from '@angular/service-worker';
import { RecaptchaV3Module } from 'ng-recaptcha-2';
import { AppRouter } from '@app/app.routes';
import { LOCALE_ID, ErrorHandler } from '@angular/core';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { OverlayContainer, FullscreenOverlayContainer } from '@angular/cdk/overlay';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import { NgxMaskConfig, provideEnvironmentNgxMask } from 'ngx-mask';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { RecaptchaService } from '@app/services/recaptcha-service';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDateFormats } from '@angular/material/core';
import { environment } from '@env/environment';
import localeCs from '@angular/common/locales/cs';
import localeCsExtra from '@angular/common/locales/extra/cs';
import 'moment/locale/cs';
import version from '../version.json';

registerLocaleData(localeCs);
registerLocaleData(localeCs, 'cs-CS', localeCsExtra);

const maskConfig: Partial<NgxMaskConfig> = {
    validation: false,
    thousandSeparator: ' ',
    decimalMarker: '.',
};

const CZECH_DATE_FORMATS: MatDateFormats = {
    parse: {
        dateInput: 'DD.MM.YYYY',
    },
    display: {
        dateInput: 'DD.MM.YYYY',
        monthYearLabel: 'MMMM Y',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM Y',
    },
};

export class CustomErrorHandler implements ErrorHandler {
    handleError(error: any) {
        const chunkFailedMessage = /Loading chunk [\d]+ failed/;

        if (chunkFailedMessage.test(error.message)) {
            window.location.reload();
        }

        console.error(error);
    }
}

if (environment.deploymentEnvironment === 'production' || environment.deploymentEnvironment === 'development') {
    datadogRum.init({
        applicationId: '71e749aa-20fc-477c-8355-89defd17c2ba',
        clientToken: 'pub2aa38abf2f5d971c808c0d2fe14cabf2',
        site: 'datadoghq.eu',
        service: 'moje.hyponamiru.cz',
        env: environment.deploymentEnvironment === 'production' ? 'prod' : 'dev',
        version: version.buildVersion,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        defaultPrivacyLevel: 'mask-user-input',
    });
}

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(
            BrowserAnimationsModule,
            AppRouter,
            RouterModule,
            Angulartics2Module.forRoot({
                gtm: {
                    userId: 'GTM-M84ZZZS',
                },
            }),
            ServiceWorkerModule.register('ngsw-worker.js', {
                enabled: environment.pwa,
                registrationStrategy: 'registerImmediately',
            }),
            RecaptchaV3Module
        ),
        provideEnvironmentNgxMask(maskConfig),
        provideHttpClient(withInterceptorsFromDi()),
        provideMomentDateAdapter({
            parse: {
                dateInput: 'DD.MM.YYYY',
            },
            display: {
                dateInput: 'DD.MM.YYYY',
                monthYearLabel: 'MMMM YYYY',
                dateA11yLabel: 'LL',
                monthYearA11yLabel: 'MMMM YYYY',
            },
        }),
        {
            provide: LOCALE_ID,
            useValue: 'cs-CZ',
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: CZECH_DATE_FORMATS,
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'cs-CS',
        },
        {
            provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
            useValue: {
                useUtc: true,
            },
        },
        {
            provide: ErrorHandler,
            useClass: CustomErrorHandler,
        },
        {
            provide: OverlayContainer,
            useClass: FullscreenOverlayContainer,
        },
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useValue: environment.captchaSiteKey,
        },
        RecaptchaService,
    ],
}).catch((err) => console.error(err));
